import { Link } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Footer } from '@/components/Footer';
import { Navbar } from '@/components/Navbar';
import { AdBanner } from '@/components/AdBanner';
import { ResponsiveAdBanner } from '@/components/ResponsiveAdBanner';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen gradient-surface gradient-mesh flex flex-col">
      <Navbar />
      
      <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
        <div className="glass-strong rounded-3xl p-6 lg:p-10 glow-border">
          <div className="flex items-center gap-4 mb-8">
            <Link to="/">
              <Button variant="ghost" size="icon" className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="w-12 h-12 rounded-2xl gradient-bg flex items-center justify-center">
              <Shield className="w-6 h-6 text-primary-foreground" />
            </div>
            <h1 className="text-3xl font-bold">Privacy Policy</h1>
          </div>

          <div className="prose prose-neutral dark:prose-invert max-w-none space-y-6">
            <p className="text-muted-foreground">Last updated: January 2025</p>

            <section>
              <h2 className="text-xl font-semibold mb-3">1. Introduction</h2>
              <p className="text-muted-foreground leading-relaxed">
                Welcome to TempVault. We respect your privacy and are committed to protecting your personal data. 
                This privacy policy explains how we handle information when you use our disposable email service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. What We Collect</h2>
              <p className="text-muted-foreground leading-relaxed">
                TempVault is designed with privacy in mind. We collect minimal data:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 mt-3">
                <li>Temporary email addresses generated for you</li>
                <li>Emails received at your temporary address (automatically deleted after 24 hours)</li>
                <li>Basic usage analytics (non-personally identifiable)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. Data Retention</h2>
              <p className="text-muted-foreground leading-relaxed">
                All emails and inbox data are automatically deleted after 24 hours. We do not store any 
                personal information beyond this period. Our system is designed to be ephemeral by nature.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Advertising & Third-Party Services</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                We use Google AdSense to display advertisements on our website. This helps us keep TempVault 
                free for all users. Here's what you should know about our advertising practices:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2">
                <li><strong>Google AdSense:</strong> We partner with Google to show relevant advertisements. Google may use cookies to serve ads based on your prior visits to our website or other websites.</li>
                <li><strong>Cookies:</strong> Google uses cookies (such as the DART cookie) to serve ads based on your browsing history. You can opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Ads Settings</a>.</li>
                <li><strong>Third-Party Vendors:</strong> Third-party vendors, including Google, use cookies to serve ads based on your prior visits to this website or other websites.</li>
                <li><strong>Your Choices:</strong> You can manage your cookie preferences through our cookie consent banner or visit <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">aboutads.info</a> to opt out of third-party advertising cookies.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Cookies We Use</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                We use the following types of cookies:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2">
                <li><strong>Essential Cookies:</strong> Required for basic site functionality (e.g., session management, terms acceptance).</li>
                <li><strong>Functional Cookies:</strong> Remember your preferences like theme settings.</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website.</li>
                <li><strong>Advertising Cookies:</strong> Used by Google AdSense to display personalized ads.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Security</h2>
              <p className="text-muted-foreground leading-relaxed">
                We implement industry-standard security measures to protect your data. All communications 
                are encrypted, and we regularly update our security practices to ensure your information 
                remains protected.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Children's Privacy</h2>
              <p className="text-muted-foreground leading-relaxed">
                TempVault is not intended for use by children under the age of 13. We do not knowingly 
                collect personal information from children under 13.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have any questions about this Privacy Policy, please contact us through our{' '}
                <Link to="/contact" className="text-primary hover:underline">Contact page</Link>.
              </p>
            </section>

            {/* Ad Banners */}
            <div className="space-y-3 mt-8">
              <div className="glass rounded-2xl p-4">
                <AdBanner adFormat="horizontal" />
              </div>
              <div className="glass rounded-2xl p-4">
                <ResponsiveAdBanner variant="stacked" />
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
